import { ARCHETYPES, type Archetype, type ZoneDistribution } from './archetypes';
import { dailySeedHash, seedToInt32 } from './daily-seed';
import type { ShotResult } from './local-state';
import { ZONES, type Zone } from './zones';

/**
 * Deterministic per-date archetype selection. See spec §4.4.
 */
export async function dailyArchetype(date: string): Promise<Archetype> {
  const hash = await dailySeedHash(date);
  const archetypeIndex = seedToInt32(hash, 0) % ARCHETYPES.length;
  return ARCHETYPES[archetypeIndex]!;
}

/**
 * Tiny seedable PRNG (mulberry32). Stateful — call returns a new uint32 each
 * time. Sufficient for fair zone sampling; not cryptographic.
 */
export function makePRNG(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Alias for makePRNG; exposed so tests can import by the canonical PRNG name. */
export const mulberry32 = makePRNG;

/**
 * Samples a single zone from a zone distribution using the supplied PRNG.
 * Per-shot client-side. The keeper does NOT have memory across shots.
 * Takes a plain `ZoneDistribution` so callers can sample from a mutated
 * mid-round distribution (see Plan #5 §3.6), not just the immutable archetype.
 * See spec §2.4.
 */
export function sampleKeeperZone(dist: ZoneDistribution, prng: () => number): Zone {
  const r = prng();
  let cumulative = 0;
  for (const zone of ZONES) {
    cumulative += dist[zone];
    if (r < cumulative) return zone;
  }
  // Floating-point guard — return last zone if we somehow exceed 1.0.
  return ZONES[ZONES.length - 1]!;
}

/**
 * Apply mood-shift after an attempt in `attemptZone`. Pure: returns a new
 * distribution. Clamp guarantees: result sums to 1.0 (within 1e-9),
 * attempt-zone never exceeds 1.0. Math is unchanged from the previous
 * "goal-only" semantics — only the calling convention now treats this as
 * "any non-off attempt by the player".
 * See spec §3.2 (Plan #5) and Plan #8 §2 for the trigger change.
 */
export function applyMoodShift(
  dist: ZoneDistribution,
  attemptZone: Zone,
  delta: number,
): ZoneDistribution {
  const pZ = dist[attemptZone];
  const otherMass = 1 - pZ;
  if (otherMass <= 0) return { ...dist };
  const applied = Math.min(delta, otherMass);
  const scale = (otherMass - applied) / otherMass;
  const next: ZoneDistribution = { LF: 0, CF: 0, RF: 0, LA: 0, CA: 0, RA: 0 };
  for (const z of ZONES) {
    next[z] = z === attemptZone ? pZ + applied : dist[z] * scale;
  }
  if (import.meta.env.DEV) {
    const sum = ZONES.reduce((a, z) => a + next[z], 0);
    if (Math.abs(sum - 1) > 1e-9) {
      throw new Error(`applyMoodShift broke probability sum: ${sum}`);
    }
  }
  return next;
}

/**
 * Replay the round's shots from `archetype` to derive the keeper's mid-round
 * distribution. Used on bfcache restore — no separate persistence field needed
 * because shots already carry `actualZone`. Pure / deterministic.
 *
 * Plan #8: mood-shift now fires on every non-'off' attempt, keyed on the
 * player's `actualZone`. Previously: only on goals, keyed on `keeperZone`.
 * See `docs/superpowers/specs/2026-05-19-keeper-mood-shift-on-attempts-design.md`.
 */
export function reconstructDistribution(
  archetype: Archetype,
  shots: readonly ShotResult[],
  delta: number,
): ZoneDistribution {
  let dist: ZoneDistribution = { ...archetype.distribution };
  for (const shot of shots) {
    if (shot.actualZone !== 'off') {
      dist = applyMoodShift(dist, shot.actualZone, delta);
    }
  }
  if (import.meta.env.DEV) {
    const sum = ZONES.reduce((a, z) => a + dist[z], 0);
    if (Math.abs(sum - 1) > 1e-9) {
      throw new Error(`reconstructDistribution broke probability sum: ${sum}`);
    }
  }
  return dist;
}
